/**
 * HomeDashboard — Phase 5.5
 *
 * Role-aware overview landing at `/`. Replaces the old AuthorWorkbench-as-home.
 *
 * Layout:
 *   1. Greeting banner (name + tenant + primary role chip)
 *   2. KPI tile grid (3-6 tiles, picked by role)
 *   3. Two-column body — left = "my work" feed, right = quick actions
 *
 * Role mapping (a user may match many; each KPI is shown if ANY of its roles
 * is in the user's role set — `Reader` is the catch-all default).
 *
 * URS rows: UX-001..005 (dual-brand chrome shown by AppShell), UX-015 (role-
 *           aware UI), UX-019..020 (RTL).
 *
 * Data: re-uses existing hooks against mock-api. Missing endpoints fall back
 *       to a zero/empty state — the dashboard NEVER blanks because one panel
 *       is slow or unreachable.
 */

import { useEffect, useMemo, useState } from 'react';
import { buildHeaders, apiUrl } from '../../api/client';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import styles from './HomeDashboardV1.module.css';
import { AiAdvisoryChip } from '../../components/AiAdvisoryChip';
import { SitesAtGlance } from '../../components/SitesAtGlance';
import {
  AiDailyBriefing,
  DepartmentsAtGlance,
  ComplianceHealthScore,
  PendingSignatures,
  UpcomingDeadlines,
} from '../../components/HomeDashboardCards';
import { useSession } from '../../auth/SessionProvider';
import {
  useCurrentUser,
  useDocuments,
  useActiveWorkflows,
  useMyTasks,
  useTrainingStats,
  useQAPendingApprovals,
  useTrainingImpactWarnings,
} from '../../api/hooks';

/* -------------------------------------------------------------------------- */
/* Role bucket helpers                                                        */
/* -------------------------------------------------------------------------- */

const ROLE_PRIORITY: Array<{ role: string; label: string; tint?: string }> = [
  { role: 'QualityDirector',      label: 'Quality Director' },
  { role: 'QADirector',           label: 'QA Director' },
  { role: 'QAManager',            label: 'QA Manager' },
  { role: 'QAApprover',           label: 'QA Approver' },
  { role: 'ValidationLead',       label: 'Validation Lead' },
  { role: 'SupplierManager',      label: 'Supplier Manager' },
  { role: 'ProcurementOfficer',   label: 'Procurement Officer' },
  { role: 'CalibrationTechnician',label: 'Calibration Technician' },
  { role: 'MaintenanceEngineer',  label: 'Maintenance Engineer' },
  { role: 'DocController',        label: 'Document Controller' },
  { role: 'Author',               label: 'Author' },
  { role: 'Inspector',            label: 'Inspector' },
  { role: 'TenantAdmin',          label: 'Tenant Admin' },
  { role: 'SystemAdmin',          label: 'System Admin' },
];

function pickPrimaryRole(roles: string[]): { role: string; label: string } {
  const set = new Set(roles);
  for (const r of ROLE_PRIORITY) if (set.has(r.role)) return r;
  return { role: 'Reader', label: 'Reader' };
}

function hasAnyRole(userRoles: Set<string>, allowed: string[]): boolean {
  return allowed.some((r) => userRoles.has(r));
}

/* -------------------------------------------------------------------------- */
/* KPI tile spec                                                              */
/* -------------------------------------------------------------------------- */

type KpiTone = 'neutral' | 'warn' | 'danger' | 'success';

interface KpiTile {
  id: string;
  to: string;
  label: string;
  icon: string;
  value: number | string | null;       // null = loading
  hint?: string;
  tone?: KpiTone;
  /** roles eligible for this tile; empty = visible to all */
  roles?: string[];
}

interface QuickAction {
  id: string;
  to: string;
  label: string;
  icon: string;
  hint?: string;
  roles?: string[];
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * v0.12.5 — backup of the v0.9/v0.11 home dashboard. Accessible at
 * /home-v1 in case Mohanad prefers it over the v0.12.5 redesign that
 * lives at /. Identical to the prior HomeDashboard except for the
 * function name + CSS module reference.
 */
export function HomeDashboardV1() {
  const { t, i18n } = useTranslation();
  const sessionCtx = useSession();
  const userRolesArr = sessionCtx.session?.roles ?? [];
  const userRoles = useMemo(() => new Set(userRolesArr), [userRolesArr]);
  const primary = pickPrimaryRole(userRolesArr);

  // ---- Queries (each panel handles its own loading/error state) ----
  const userQ        = useCurrentUser();
  const docsQ        = useDocuments();
  const workflowsQ   = useActiveWorkflows();
  const tasksQ       = useMyTasks();
  const trainingQ    = useTrainingStats();
  const qaPendingQ   = useQAPendingApprovals();
  const trainingImpQ = useTrainingImpactWarnings();

  // ---- Derived counts (treat missing data as zero, not error) ----
  const drafts = (docsQ.data?.items ?? []).filter(
    (d) => d.status === 'DRAFT' || d.status === 'REVIEW_REJECTED',
  );
  const activeWorkflows = workflowsQ.data?.items ?? [];
  const myTasks         = tasksQ.data?.items ?? [];
  const qaPending       = qaPendingQ.data ?? [];
  const trainingImpacts = trainingImpQ.data ?? [];
  const slaBreaches     = activeWorkflows.filter((w) => w.sla_state === 'breach');
  const trainingPct     = trainingQ.data?.completion_pct ?? null;
  const overdueTraining = trainingQ.data?.overdue_count ?? null;

  // ---- KPI definitions (the dashboard picks the eligible subset) ----
  const allKpis: KpiTile[] = [
    // Author / Reader-default
    {
      id: 'my-drafts', to: '/workbench', icon: '📝',
      label: t('home.kpi.myDrafts', { defaultValue: 'My drafts' }),
      value: docsQ.isLoading ? null : drafts.length,
      hint: drafts.length === 0
        ? t('home.kpi.draftsEmpty', { defaultValue: 'No drafts in progress' })
        : t('home.kpi.draftsHint', { defaultValue: 'Open & unfinished', count: drafts.length }),
      tone: 'neutral',
    },
    {
      id: 'my-tasks', to: '/training', icon: '✓',
      label: t('home.kpi.myTasks', { defaultValue: 'My tasks' }),
      value: tasksQ.isLoading ? null : myTasks.length,
      hint: myTasks.length === 0
        ? t('home.kpi.tasksEmpty', { defaultValue: 'Inbox is clear' })
        : t('home.kpi.tasksHint', { defaultValue: 'Pending in your queue' }),
      tone: myTasks.length > 0 ? 'warn' : 'success',
    },
    {
      id: 'training-currency', to: '/training', icon: '🎓',
      label: t('home.kpi.training', { defaultValue: 'Training currency' }),
      value: trainingPct == null ? null : `${trainingPct}%`,
      hint: overdueTraining == null
        ? undefined
        : overdueTraining === 0
          ? t('home.kpi.trainingCurrent', { defaultValue: 'You are current' })
          : t('home.kpi.trainingOverdue', { defaultValue: '{{n}} overdue', n: overdueTraining }),
      tone: overdueTraining && overdueTraining > 0 ? 'danger' : 'success',
    },

    // QA Manager / Director
    {
      id: 'qa-pending', to: '/qa', icon: '📥',
      label: t('home.kpi.qaPending', { defaultValue: 'Pending QA approvals' }),
      value: qaPendingQ.isLoading ? null : qaPending.length,
      hint: qaPending.length === 0
        ? t('home.kpi.qaEmpty', { defaultValue: 'QA queue is empty' })
        : t('home.kpi.qaHint',  { defaultValue: 'Workflows waiting on QA' }),
      tone: qaPending.length > 0 ? 'warn' : 'success',
      roles: ['QAManager', 'QAApprover', 'QADirector'],
    },
    {
      id: 'sla-breaches', to: '/qa', icon: '⚠️',
      label: t('home.kpi.slaBreaches', { defaultValue: 'SLA breaches' }),
      value: workflowsQ.isLoading ? null : slaBreaches.length,
      hint: slaBreaches.length === 0
        ? t('home.kpi.slaClear', { defaultValue: 'All workflows in SLA' })
        : t('home.kpi.slaWarn',  { defaultValue: 'Workflows past SLA' }),
      tone: slaBreaches.length > 0 ? 'danger' : 'success',
      roles: ['QAManager', 'QADirector'],
    },
    {
      id: 'training-impacts', to: '/qa', icon: '👥',
      label: t('home.kpi.trainingImpacts', { defaultValue: 'Training-impacting workflows' }),
      value: trainingImpQ.isLoading ? null : trainingImpacts.length,
      hint: t('home.kpi.trainingImpactsHint', { defaultValue: 'Reassignments queued on QA-Approval' }),
      tone: trainingImpacts.length > 0 ? 'warn' : 'neutral',
      roles: ['QAManager', 'QADirector'],
    },
    {
      id: 'metrics-dashboard', to: '/quality-metrics', icon: '📊',
      label: t('home.kpi.metricsDashboard', { defaultValue: 'Quality metrics' }),
      value: '—',
      hint: t('home.kpi.metricsHint', { defaultValue: 'Open the trend board' }),
      tone: 'neutral',
      roles: ['QAManager', 'QADirector', 'QualityAnalyticsViewer', 'SiteQualityHead'],
    },

    // Supplier roles
    {
      id: 'supplier-risk', to: '/suppliers/risk-ranking', icon: '🏭',
      label: t('home.kpi.supplierRisk', { defaultValue: 'Supplier risk ranking' }),
      value: '—',
      hint: t('home.kpi.supplierRiskHint', { defaultValue: 'Top-N risk view' }),
      tone: 'neutral',
      roles: ['ProcurementOfficer', 'SupplierManager', 'QAManager', 'QADirector'],
    },

    // Equipment / Validation roles
    {
      id: 'equipment-requal', to: '/equipment/requalification-schedule', icon: '🛠️',
      label: t('home.kpi.equipmentRequal', { defaultValue: 'Equipment requalifications' }),
      value: '—',
      hint: t('home.kpi.equipmentRequalHint', { defaultValue: 'Due in the next 90 days' }),
      tone: 'neutral',
      roles: ['ValidationLead', 'QAManager'],
    },
    {
      id: 'calibrations', to: '/equipment/calibrations', icon: '🔬',
      label: t('home.kpi.calibrations', { defaultValue: "Today's calibrations" }),
      value: '—',
      hint: t('home.kpi.calibrationsHint', { defaultValue: 'Open calibration tray' }),
      tone: 'neutral',
      roles: ['CalibrationTechnician', 'ValidationLead'],
    },
    {
      id: 'pm-jobs', to: '/equipment/pm', icon: '🔧',
      label: t('home.kpi.pmJobs', { defaultValue: 'Open PM jobs' }),
      value: '—',
      hint: t('home.kpi.pmHint', { defaultValue: 'Preventive maintenance tray' }),
      tone: 'neutral',
      roles: ['MaintenanceEngineer', 'ValidationLead'],
    },

    // Doc Controller
    {
      id: 'ocr-queue', to: '/ocr/review', icon: '🔍',
      label: t('home.kpi.ocrQueue', { defaultValue: 'OCR review queue' }),
      value: '—',
      hint: t('home.kpi.ocrHint', { defaultValue: 'Open OCR review' }),
      tone: 'neutral',
      roles: ['DocController', 'QAManager'],
    },
  ];

  // Filter KPIs: a tile is visible if it has no role gate OR the user holds
  // at least one of its roles. Reader-default tiles (no `roles`) always show.
  const visibleKpis = allKpis.filter((k) => !k.roles || hasAnyRole(userRoles, k.roles));

  // ---- Quick actions (right column) ----
  const allActions: QuickAction[] = [
    { id: 'new-document', to: '/workbench',   icon: '📄',
      label: t('home.actions.newDocument',    { defaultValue: 'New document' }),
      hint:  t('home.actions.newDocumentHint',{ defaultValue: 'Draft a new SOP / WI' }) },
    { id: 'propose-change', to: '/changes/new', icon: '🔁',
      label: t('home.actions.proposeChange',     { defaultValue: 'Propose change request' }),
      hint:  t('home.actions.proposeChangeHint', { defaultValue: 'Start a CC proposal' }),
      roles: ['Author', 'QAManager', 'QAApprover', 'ValidationLead', 'ProcurementOfficer'] },
    { id: 'training', to: '/training', icon: '🎓',
      label: t('home.actions.training',     { defaultValue: 'My training inbox' }),
      hint:  t('home.actions.trainingHint', { defaultValue: 'R&U + quiz tasks' }) },
    { id: 'inspector', to: '/inspector/audit', icon: '👁️',
      label: t('home.actions.inspector',     { defaultValue: 'Open Inspector' }),
      hint:  t('home.actions.inspectorHint', { defaultValue: 'Read-only audit trail' }),
      roles: ['Inspector', 'QAManager', 'QADirector', 'AuditorInternal'] },
    { id: 'cmd-k', to: '/', icon: '⌕',
      label: t('home.actions.search',     { defaultValue: 'Search or jump to' }),
      hint:  t('home.actions.searchHint', { defaultValue: '⌘K' }) },
  ];
  const visibleActions = allActions.filter((a) => !a.roles || hasAnyRole(userRoles, a.roles));

  // ---- AI-309 — BMR review queue (top-N by AI risk score) ----
  const [bmrQueue, setBmrQueue] = useState<Array<{ bmr_execution_id: string; batch_code: string; product_code: string; status: string; riskScore: number; findingsCount: number }>>([]);
  useEffect(() => {
    let cancelled = false;
    fetch(apiUrl('/api/v1/bmr/review-stats'), { headers: buildHeaders() })
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setBmrQueue((d.awaitingReview || []).slice(0, 5)); })
      .catch(() => { /* tolerate */ });
    return () => { cancelled = true; };
  }, []);

  // ---- AI-103 inbox-triage state ----
  // applyAiTriage() POSTs the feed to /api/v1/ai/advisory and gets back a
  // priority-ordered list. We reorder the feed by that list when present.
  const [aiTriageOrder, setAiTriageOrder] = useState<string[] | null>(null);
  const [aiTriageReason, setAiTriageReason] = useState<string | null>(null);
  const [aiTriageBusy, setAiTriageBusy] = useState(false);

  // ---- "Active work" feed: blend QA-pending (if QA) + my drafts + my tasks ----
  type FeedItem = { id: string; title: string; meta: string; to: string; icon: string; badge?: { label: string; tone?: 'warn' | 'danger' } };
  const feed: FeedItem[] = [];
  if (hasAnyRole(userRoles, ['QAManager', 'QAApprover', 'QADirector'])) {
    for (const w of qaPending.slice(0, 4)) {
      feed.push({
        id: `qa-${w.workflowId}`,
        title: `${w.documentNumber} — ${w.title}`,
        meta: t('home.feed.qaMeta', { defaultValue: 'QA-Approval · by {{author}} · {{time}}', author: w.authorName, time: w.timeInState }),
        to: `/workflows/${w.workflowId}`,
        icon: '✍️',
        badge: { label: 'QA-Approval', tone: 'warn' },
      });
    }
  }
  for (const wf of activeWorkflows.slice(0, 3)) {
    feed.push({
      id: `wf-${wf.id}`,
      title: `${wf.doc_number}`,
      meta: t('home.feed.wfMeta', { defaultValue: '{{stage}} · SLA {{sla}}', stage: wf.stage, sla: wf.sla }),
      to: `/workflows/${wf.id}`,
      icon: '📂',
      badge: wf.sla_state === 'breach'
        ? { label: t('home.feed.slaBreached', { defaultValue: 'SLA breached' }), tone: 'danger' }
        : wf.sla_state === 'warn'
          ? { label: t('home.feed.slaWarn', { defaultValue: 'SLA at risk' }), tone: 'warn' }
          : undefined,
    });
  }
  for (const d of drafts.slice(0, 3)) {
    feed.push({
      id: `draft-${d.id}`,
      title: `${d.doc_number} — ${d.title || d.doc_type_code}`,
      meta: t('home.feed.draftMeta', { defaultValue: 'Draft · saved {{time}}', time: d.last_saved }),
      to: `/workflows/${d.id}`,
      icon: '📝',
    });
  }
  for (const tk of myTasks.slice(0, 3)) {
    feed.push({
      id: `task-${tk.id}`,
      title: tk.label,
      meta: t('home.feed.taskMeta', { defaultValue: 'Task · due {{due}}', due: tk.due }),
      to: '/training',
      icon: '⏰',
      badge: tk.urgent
        ? { label: t('home.feed.urgent', { defaultValue: 'Urgent' }), tone: 'danger' }
        : undefined,
    });
  }

  // AI-103: reorder feed if AI triage ordering is present.
  const orderedFeed = useMemo(() => {
    if (!aiTriageOrder || aiTriageOrder.length === 0) return feed;
    const byId = new Map(feed.map((f) => [f.id, f]));
    const ordered: FeedItem[] = [];
    for (const id of aiTriageOrder) {
      const f = byId.get(id);
      if (f) { ordered.push(f); byId.delete(id); }
    }
    // Append anything the AI didn't mention (keeps stuff visible).
    for (const f of byId.values()) ordered.push(f);
    return ordered;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiTriageOrder, feed.length, feed.map((f) => f.id).join('|')]);

  const applyAiTriage = async () => {
    if (feed.length === 0 || aiTriageBusy) return;
    setAiTriageBusy(true);
    try {
      const promptItems = feed.map((f) => ({
        id: f.id,
        title: f.title,
        meta: f.meta,
        badge: f.badge?.label,
        badgeTone: f.badge?.tone,
      }));
      const res = await fetch(apiUrl('/api/v1/ai/advisory'), {
        method: 'POST',
        headers: buildHeaders({ json: true }),
        body: JSON.stringify({
          surface: 'dashboard.inbox-triage',
          prompt: `Reorder the following ${feed.length} pending action items in the most useful priority order for a quality-team reviewer. ` +
                  `Place SLA-at-risk + danger badges first, then aging items, then routine items. ` +
                  `Return a JSON object { "prioritized": [{ "id": "...", "reason": "..." }] } using the exact ids supplied. ` +
                  `Items: ${JSON.stringify(promptItems)}`,
          jsonSchema: {
            type: 'object',
            properties: {
              prioritized: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    id:     { type: 'string' },
                    reason: { type: 'string' },
                  },
                  required: ['id'],
                },
              },
            },
            required: ['prioritized'],
          },
          cacheKey: `inbox-triage:${feed.length}:${feed.map((f) => f.id).join(',')}`,
        }),
      });
      const data = await res.json();
      const payload = data?.response?.payload;
      const list: Array<{ id: string; reason?: string }> = Array.isArray(payload?.prioritized) ? payload.prioritized : [];
      if (list.length > 0) {
        setAiTriageOrder(list.map((x) => x.id));
        setAiTriageReason(list[0]?.reason ? `Top of queue: ${list[0]?.reason}` : 'AI ranked your queue');
      } else {
        setAiTriageReason('AI did not return a ranking — original order preserved.');
      }
    } catch (err) {
      setAiTriageReason(`AI triage failed: ${(err as Error).message}`);
    } finally {
      setAiTriageBusy(false);
    }
  };
  const clearAiTriage = () => { setAiTriageOrder(null); setAiTriageReason(null); };

  // ---- Greeting message (locale-aware) ----
  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 5)  return t('home.greeting.night',   { defaultValue: 'Working late' });
    if (hour < 12) return t('home.greeting.morning', { defaultValue: 'Good morning' });
    if (hour < 18) return t('home.greeting.day',     { defaultValue: 'Good afternoon' });
    return t('home.greeting.evening', { defaultValue: 'Good evening' });
  }, [t, i18n.language]);

  const fullName = userQ.data?.full_name
    ?? sessionCtx.session?.preferredUsername
    ?? '…';

  const tenantName = userQ.data?.tenant ?? 'Spectropharma';

  return (
    <div className={styles.page} dir={i18n.language.startsWith('ar') ? 'rtl' : 'ltr'}>
      {/* Greeting banner */}
      <header className={styles.greeting}>
        <h1 className={styles.greetingHi}>
          {greeting}, {fullName.split(' ')[0]}
          <span className={styles.greetingRole}>{userQ.data?.role || primary.label}</span>
        </h1>
        <p className={styles.greetingSub}>
          {t('home.greeting.sub', {
            defaultValue: '{{tenant}} · {{count}} action(s) waiting',
            tenant: tenantName,
            count: feed.length,
          })}
        </p>
      </header>

      {/* v0.9.1 — AI Daily Briefing hero card (one-paragraph morning summary) */}
      <AiDailyBriefing />

      {/* Sites at a glance — V26 multi-site row */}
      <SitesAtGlance />

      {/* v0.9.1 — Two-column split: Compliance Health Score + Pending Signatures */}
      <div className={styles.scoreSigRow}>
        <ComplianceHealthScore />
        <PendingSignatures />
      </div>

      {/* v0.9.1 — Departments at a glance */}
      <DepartmentsAtGlance />

      {/* v0.9.1 — Upcoming GxP Deadlines strip */}
      <UpcomingDeadlines />

      {/* KPI grid */}
      <section className={styles.kpiGrid} aria-label={t('home.kpiGridLabel', { defaultValue: 'Key indicators' })}>
        {visibleKpis.map((k) => (
          <Link
            key={k.id}
            to={k.to}
            className={`${styles.kpiTile} ${toneClass(k.tone)}`}
            aria-label={`${k.label} ${k.value ?? ''}`}
          >
            <div className={styles.kpiHead}>
              <span className={styles.kpiLabel}>{k.label}</span>
              <span className={styles.kpiIcon} aria-hidden="true">{k.icon}</span>
            </div>
            <div className={styles.kpiValue}>
              {k.value == null
                ? <span className={styles.kpiValueLoading} aria-label="loading" />
                : k.value}
            </div>
            {k.hint && <div className={styles.kpiHint}>{k.hint}</div>}
          </Link>
        ))}
      </section>

      {/* AI-309 — BMRs awaiting your review (Phase 3) */}
      {bmrQueue.length > 0 && hasAnyRole(userRoles, ['QAManager', 'QADirector', 'QAApprover']) && (
        <section className={styles.bmrQueueSection} aria-label="BMRs awaiting your review">
          <header className={styles.sectionHead}>
            <h2 className={styles.sectionTitle}>
              {t('home.bmr.title', { defaultValue: 'BMRs awaiting your review' })}
              <span className={styles.bmrCount}> ({bmrQueue.length})</span>
            </h2>
            <span className={styles.bmrCaption}>ranked by AI risk score</span>
          </header>
          <ul className={styles.bmrList}>
            {bmrQueue.map((b) => (
              <li key={b.bmr_execution_id}>
                <Link to="/quality/bmr-review/$id" params={{ id: b.bmr_execution_id }} className={styles.bmrRow}>
                  <span className={styles.bmrBatch}>{b.batch_code}</span>
                  <span className={styles.bmrProduct}>{b.product_code}</span>
                  <span className={styles.bmrFindings}>{b.findingsCount} findings</span>
                  <span className={`${styles.bmrRisk} ${b.riskScore >= 4 ? styles.bmrRiskHigh : b.riskScore >= 2 ? styles.bmrRiskMed : styles.bmrRiskLow}`}>
                    risk {b.riskScore}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* AI-101 + AI-102 — AI insights row, QA-roles only */}
      {hasAnyRole(userRoles, ['QAManager', 'QADirector', 'QAApprover']) && (
        <section className={styles.aiInsightsRow} aria-label="AI insights">
          <AiAdvisoryChip
            surface="dashboard.sla-forecast"
            prompt={
              `You are reviewing the Spectropharma QA Director's home dashboard. ` +
              `Active workflows: ${activeWorkflows.length}. SLA breaches now: ${slaBreaches.length}. ` +
              `QA approvals pending: ${qaPending.length}. ` +
              `Predict the SLA-breach risk over the next 24 hours in one sentence, then list 2–3 specific concerns ` +
              `(workflow IDs, document numbers, or trends). Return JSON {summary, concerns:[]}.`
            }
            cacheKey={`sla-forecast:${activeWorkflows.length}:${slaBreaches.length}:${qaPending.length}`}
            jsonSchema={{
              type: 'object',
              properties: {
                summary:  { type: 'string' },
                concerns: { type: 'array', items: { type: 'string' } },
              },
              required: ['summary'],
            }}
            citations={activeWorkflows.slice(0, 3).map((w) => ({
              kind: 'workflow', id: String(w.id), label: w.doc_number, href: `/workflows/${w.id}`,
            }))}
          />
          <AiAdvisoryChip
            surface="dashboard.kpi-anomaly"
            prompt={
              `Spectropharma KPI snapshot — training currency: ${trainingPct ?? '—'}%. ` +
              `Overdue training: ${overdueTraining ?? 0}. SLA breaches: ${slaBreaches.length}. ` +
              `Active workflows: ${activeWorkflows.length}. QA pending: ${qaPending.length}. ` +
              `Drafts in progress: ${drafts.length}. ` +
              `Flag any anomalies vs. typical pharma quality KPIs (cite Annex 1, ICH Q10 where relevant). ` +
              `One sentence + 2–3 anomaly bullets. Return JSON {summary, concerns:[]}.`
            }
            cacheKey={`kpi-anomaly:${trainingPct ?? 'n'}:${slaBreaches.length}:${activeWorkflows.length}`}
            jsonSchema={{
              type: 'object',
              properties: {
                summary:  { type: 'string' },
                concerns: { type: 'array', items: { type: 'string' } },
              },
              required: ['summary'],
            }}
          />
        </section>
      )}

      {/* Body: feed (left) + actions (right) */}
      <div className={styles.body}>
        <section className={styles.section} aria-labelledby="home-feed-title">
          <header className={styles.sectionHead}>
            <h2 id="home-feed-title" className={styles.sectionTitle}>
              {t('home.feed.title', { defaultValue: 'Your active work' })}
            </h2>
            <div className={styles.sectionHeadActions}>
              {/* AI-103 — Re-sort feed by AI inbox-triage */}
              {feed.length > 1 && (aiTriageOrder
                ? (
                  <button type="button" className={styles.aiTriageBtn} onClick={clearAiTriage}>
                    {t('home.feed.aiTriageReset', { defaultValue: 'Reset order' })}
                  </button>
                )
                : (
                  <button type="button" className={styles.aiTriageBtn} onClick={applyAiTriage} disabled={aiTriageBusy}>
                    {aiTriageBusy
                      ? t('home.feed.aiTriageBusy', { defaultValue: 'Sorting…' })
                      : t('home.feed.aiTriage', { defaultValue: '✦ Re-sort with AI' })}
                  </button>
                )
              )}
              {hasAnyRole(userRoles, ['QAManager', 'QAApprover', 'QADirector']) ? (
                <Link to="/qa" className={styles.sectionMore}>
                  {t('home.feed.moreQA', { defaultValue: 'Open QA Workbench →' })}
                </Link>
              ) : (
                <Link to="/training" className={styles.sectionMore}>
                  {t('home.feed.moreTraining', { defaultValue: 'Open training →' })}
                </Link>
              )}
            </div>
          </header>
          {aiTriageReason && (
            <p className={styles.aiTriageReason} role="status">{aiTriageReason}</p>
          )}
          {orderedFeed.length === 0 ? (
            <div className={styles.empty}>
              {t('home.feed.empty', { defaultValue: 'Nothing waiting on you right now.' })}
            </div>
          ) : (
            <ul className={styles.feedList}>
              {orderedFeed.map((f) => (
                <li key={f.id}>
                  <Link to={f.to as never} className={styles.feedRow}>
                    <span className={styles.feedIcon} aria-hidden="true">{f.icon}</span>
                    <span className={styles.feedCopy}>
                      <span className={styles.feedTitle}>{f.title}</span>
                      <span className={styles.feedMeta}>{f.meta}</span>
                    </span>
                    {f.badge && (
                      <span
                        className={`${styles.feedBadge} ${
                          f.badge.tone === 'danger' ? styles.feedBadgeDanger :
                          f.badge.tone === 'warn'   ? styles.feedBadgeWarn   : ''
                        }`}
                      >
                        {f.badge.label}
                      </span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className={styles.section} aria-labelledby="home-actions-title">
          <header className={styles.sectionHead}>
            <h2 id="home-actions-title" className={styles.sectionTitle}>
              {t('home.actions.title', { defaultValue: 'Quick actions' })}
            </h2>
          </header>
          <div className={styles.actionsList}>
            {visibleActions.map((a) => (
              <Link key={a.id} to={a.to as never} className={styles.actionRow}>
                <span className={styles.actionIcon} aria-hidden="true">{a.icon}</span>
                <span className={styles.actionLabel}>{a.label}</span>
                {a.hint && <span className={styles.actionHint}>{a.hint}</span>}
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function toneClass(tone: KpiTone | undefined): string {
  switch (tone) {
    case 'warn':    return styles.kpiTileWarn;
    case 'danger':  return styles.kpiTileDanger;
    case 'success': return styles.kpiTileSuccess;
    default:        return '';
  }
}
